/**
 * Smooth Lerp Camera with Bounds Clamping and Dynamic Screen Shake
 */

import { CANVAS_WIDTH, CANVAS_HEIGHT, MAP_WIDTH, MAP_HEIGHT } from "../config/gameConfig";

export class Camera {
  public x: number = 0;
  public y: number = 0;
  public targetX: number = 0;
  public targetY: number = 0;

  public viewportWidth: number = CANVAS_WIDTH;
  public viewportHeight: number = CANVAS_HEIGHT;

  public screenShake: number = 0;
  public shakeOffsetX: number = 0;
  public shakeOffsetY: number = 0;

  public lerpSpeed: number = 0.12;

  constructor(initialX: number = 400, initialY: number = 500) {
    this.x = initialX - this.viewportWidth / 2;
    this.y = initialY - this.viewportHeight / 2;
    this.targetX = this.x;
    this.targetY = this.y;
    this.clamp();
  }

  public setTarget(playerX: number, playerY: number) {
    this.targetX = playerX - this.viewportWidth / 2;
    this.targetY = playerY - this.viewportHeight / 2;
  }

  public addShake(amount: number) {
    this.screenShake = Math.min(24, this.screenShake + amount);
  }

  public update(dt: number) {
    // Smooth lerp toward target
    this.x += (this.targetX - this.x) * this.lerpSpeed;
    this.y += (this.targetY - this.y) * this.lerpSpeed;

    this.clamp();

    // Process screen shake with decay
    if (this.screenShake > 0) {
      this.shakeOffsetX = (Math.random() - 0.5) * this.screenShake;
      this.shakeOffsetY = (Math.random() - 0.5) * this.screenShake;
      this.screenShake = Math.max(0, this.screenShake - dt * 25);
    } else {
      this.shakeOffsetX = 0;
      this.shakeOffsetY = 0;
    }
  }

  private clamp() {
    const maxX = MAP_WIDTH - this.viewportWidth;
    const maxY = MAP_HEIGHT - this.viewportHeight;

    this.x = Math.max(0, Math.min(maxX, this.x));
    this.y = Math.max(0, Math.min(maxY, this.y));
  }

  public applyTransform(ctx: CanvasRenderingContext2D) {
    ctx.save();
    const renderX = Math.round(this.x + this.shakeOffsetX);
    const renderY = Math.round(this.y + this.shakeOffsetY);
    ctx.translate(-renderX, -renderY);
  }

  public restore(ctx: CanvasRenderingContext2D) {
    ctx.restore();
  }

  public worldToScreen(wx: number, wy: number): { x: number; y: number } {
    return {
      x: wx - this.x - this.shakeOffsetX,
      y: wy - this.y - this.shakeOffsetY,
    };
  }

  public screenToWorld(sx: number, sy: number): { x: number; y: number } {
    return {
      x: sx + this.x + this.shakeOffsetX,
      y: sy + this.y + this.shakeOffsetY,
    };
  }
}
